import { ethers } from "ethers";
import { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useMetaMask } from "src/store/metamask";

function tokenAmountToWeiAmount(tokenAmount) {
    return ethers.utils.parseUnits(tokenAmount, 18);
}

function weiAmountToTokenAmount(wei) {
    return ethers.utils.formatUnits(wei, 18);
}

const useStake = (
    stakingContract,
    apy,
    {
        handleClose,
        setOpenLoader,
        setOpenClaim,
        openClaim,
        openUnstake,
        setOpenAreyouSure,
        rewardDialogData,
    }
) => {
    const [data] = useMetaMask();
    const { signerAddress, tokenContract } = data;
    //console.log(data,'usestake')
   // console.log(stakingContract,'contract',) 
      //  console.log(apy,'apy');

   // console.log(signerAddress, tokenContract, 'testing for new');

    async function checkAllowance(amount) {
        if (!tokenContract) {
            throw new Error("Token contract not connected");
        }
        if (!signerAddress || !apy) {
            throw new Error("Wallet or staking contract address not available");
        }

        const weiAmount = tokenAmountToWeiAmount(amount);

        let userAllowance;
        try {
            userAllowance = await tokenContract.allowance(signerAddress, apy);
        } catch (err) {
            console.error("Error checking allowance:", err);
            throw new Error("Failed to check token allowance. Please try again.");
        }

        // If the allowance is less than the amount we want to stake, we need to approve the staking contract to spend our tokens
        if (userAllowance.lt(weiAmount)) {
            try {
                enqueueSnackbar("Approving tokens for staking...", {
                    variant: "info",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "top",
                    },
                });
                const approveTx = await tokenContract.approve(apy, weiAmount);
                await approveTx.wait();
                enqueueSnackbar("Token approval successful!", {
                    variant: "success",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "top",
                    },
                });
            } catch (err) {
                console.error("Error approving tokens:", err);
                if (err.message && (err.message.includes("user rejected") || err.message.includes("User denied"))) {
                    throw new Error("Token approval was cancelled.");
                }
                throw new Error("Failed to approve tokens. Please try again.");
            }
        }
    }
    async function depositStake(amount) {
        handleClose();
        setOpenLoader(true);

        try {
            if (!tokenContract || !signerAddress) {
                throw new Error("Wallet not connected");
            }

            // Convert the amount from BTAF token to wei
            // 1 BTAF token = 10^18 wei
            const weiAmount = tokenAmountToWeiAmount(amount);
           // console.log(weiAmount);

            // Check token balance before attempting to stake
            const balance = await tokenContract.balanceOf(signerAddress);
            if (balance.lt(weiAmount)) {
                const balanceFormatted = weiAmountToTokenAmount(balance);
                throw new Error(
                    `Insufficient token balance. You have ${balanceFormatted} tokens, but trying to stake ${amount} tokens.`
                );
            }

            // Check and approve allowance
            await checkAllowance(amount);

            // Call the depositStake function on the staking contract
            const tx = await stakingContract.depositStake(weiAmount);

            await tx.wait();
            setOpenLoader(false);
            enqueueSnackbar("Stake deposited successfully!", {
                variant: "success",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "top",
                },
            });
            getNumberOfStake();
        } catch (err) {
            console.error("depositStake error:", err);
            setOpenLoader(false);

            // Extract and display user-friendly error message
            let errorMessage = "Failed to deposit stake. Please try again.";

            if (err.message) {
                // If it's a custom error message we created, use it
                if (err.message.includes("Insufficient token balance") || 
                    err.message.includes("Wallet not connected")) {
                    errorMessage = err.message;
                } 
                // Check for common error patterns
                else if (err.message.includes("insufficient funds") || 
                         err.message.includes("insufficient balance")) {
                    errorMessage = "Insufficient token balance. Please check your wallet.";
                } 
                else if (err.message.includes("user rejected") || 
                         err.message.includes("User denied")) {
                    errorMessage = "Transaction was cancelled.";
                    return; // Don't show error for user cancellation
                }
                // Check for revert reason in error data
                else if (err.data || err.reason) {
                    const errorData = err.data || err.reason;
                    // Try to decode custom error (0xfb8f41b2)
                    if (typeof errorData === "string" && errorData.includes("0xfb8f41b2")) {
                        errorMessage = "Transaction failed: Insufficient balance or minimum stake requirement not met. Please check your token balance and ensure you meet the minimum stake amount.";
                    } else if (errorData) {
                        errorMessage = `Transaction failed: ${errorData}`;
                    }
                }
                // Check for UNPREDICTABLE_GAS_LIMIT which usually means the transaction will revert
                else if (err.code === "UNPREDICTABLE_GAS_LIMIT" || 
                         err.message.includes("UNPREDICTABLE_GAS_LIMIT")) {
                    errorMessage = "Transaction will fail. This usually means: insufficient token balance, minimum stake requirement not met, or contract validation failed. Please check your balance and try again.";
                }
            }

            enqueueSnackbar(errorMessage, {
                variant: "error",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "top",
                },
            });
        }
    }

    const [numberOfStake, setNumberOfStake] = useState(0);
    const [stakingInfo, setStackingInfo] = useState([]);

    const getNumberOfStake = async () => {
        if (!stakingContract || !signerAddress) {
            return false;
        }

        const getStakeCountFn = (() => {
            if (typeof stakingContract?.getUserStakeCount === "function") {
                return stakingContract.getUserStakeCount.bind(stakingContract);
            }
            if (
                typeof stakingContract?.getInvestorStakeCount === "function"
            ) {
                return stakingContract.getInvestorStakeCount.bind(
                    stakingContract
                );
            }
            return null;
        })();

        if (!getStakeCountFn) {
            console.warn(
                "Active staking contract does not expose a stake count method"
            );
            setNumberOfStake(0);
            return false;
        }

        try {
            setStackingInfo([]);
            const count = await getStakeCountFn(signerAddress);
            const formattedCount =
                typeof count?.toNumber === "function"
                    ? count.toNumber()
                    : Number(count || 0);
            setNumberOfStake(formattedCount);
            return true;
        } catch (error) {
            console.error("Failed to fetch stake count", error);
            setNumberOfStake(0);
            return false;
        }
    };

    useEffect(() => {
        if (signerAddress) {
            getNumberOfStake();
        }
    }, [signerAddress]);

    const test = async (index) => {
        const stakeInfo = await stakingContract.getInvestorStake(
            signerAddress,
            index
        );
        console.log(stakeInfo,'info')  
        //  //this line addede because the startdate and starttime  variable has been changed in the contract
        const timestamp = stakeInfo.startTime
                ? Number(stakeInfo.startTime) * 1000
                : Number(stakeInfo.startDate) * 1000;

        return {
            unStaked: stakeInfo.unstaked ?? Boolean(stakeInfo.dateWithdrawn.toNumber()),
            staker: stakeInfo.staker,
            id: index,
            amount: ethers.utils.formatUnits(stakeInfo.amount, 18),
            startDate: new Date(
                // stakeInfo.startTime.toNumber() * 1000
                timestamp
            ).toString(),
            reward: await getReward(index),
            stakeBurnt: stakeInfo.stakeBurnt ? weiAmountToTokenAmount(stakeInfo.stakeBurnt) : 0,
        };
    };

    const wrapper = async () => {
        const res = [...new Array(numberOfStake)].map(async (_, i) => {
            return test(i);
        });
        // console.log(await Promise.all(res));
        setStackingInfo(await Promise.all(res));
    };

    useEffect(() => {
        if (Boolean(numberOfStake)) {
            wrapper();
        }
    }, [numberOfStake]);

    async function claimReward() {
        // const idx = document.getElementById("claimRewardStakeId").value;
        const idx = rewardDialogData.id;
        setOpenClaim(false);
        setOpenLoader(true);
        // await setup();

        const startingBalance = await tokenContract.balanceOf(signerAddress);

        // Call the claimReward function on the staking contract
        try {
            const tx = await stakingContract.claimReward(idx);
            await tx.wait();
            const newBalance = await tokenContract.balanceOf(signerAddress);
            const claim = weiAmountToTokenAmount(
                newBalance.sub(startingBalance)
            );

             setNumberOfStake(0);
             getNumberOfStake();

            setOpenLoader(false);
        } catch (err) {
            console.error(err);
            setOpenLoader(false);
        }

        // rewardClaimed_ref.current.innerText = `${claim} reward claimed`;
        // document.getElementById(
        //   "rewardClaimed"
        // ).innerText = `${claim} reward claimed`;
    }

    async function getReward(idx) {
        
        const data = await stakingContract.previewInvestorClaimReward(
            signerAddress,
            idx
        );

        setReward(weiAmountToTokenAmount(data));
        return weiAmountToTokenAmount(data);
    }

    const [reward, setReward] = useState(0);
    useEffect(() => {
        if (openClaim) {
            getReward(rewardDialogData.id);
        }
    }, [openClaim]);
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [burnAmount, setBurnOut] = useState("");
    useEffect(() => {
        async function test(idx) {
            
            try {
                const withdrawPreview =
                    await stakingContract.previewInvestorWithdrawStake(
                        signerAddress,
                        idx
                    );
                const withdrawAmount = withdrawPreview[0];
                const burnAmount = withdrawPreview[1];
                setWithdrawAmount(weiAmountToTokenAmount(withdrawAmount));
                setBurnOut(weiAmountToTokenAmount(burnAmount));
            } catch (err) {
                console.log(err.message);
            }

            
        }
        // setNumberOfStake(0);
        // getNumberOfStake();

        if (openUnstake) {
            test(rewardDialogData.id);
        }
    }, [openUnstake]);

    async function withdrawStake() {
        setOpenAreyouSure(false);
        handleClose();
        setOpenLoader(true);
        const idx = rewardDialogData.id;

        // await setup();

        const stake = await stakingContract.getStake(idx);
        const startingBalance = await tokenContract.balanceOf(signerAddress);

        // Call the withdrawStake function on the staking contract
        const tx = await stakingContract.withdrawStake(idx);
        await tx.wait();
        setNumberOfStake(0);
        getNumberOfStake();
        setOpenLoader(false);
        
    }

    return {
        depositStake,
        stakingInfo,
        withdrawStake,
        withdrawAmount,
        burnAmount,
        reward,
        claimReward,
        getNumberOfStake,
        setNumberOfStake,
    };
};

export default useStake;